import { ChangeEvent, useRef, useState } from 'react';

import styled from 'styled-components';

import { Avatar } from '@components/avatar';
import { IconEdit } from '@components/icons/edit';
import { IconImage } from '@components/icons/image';
import { IconRemove } from '@components/icons/remove';
import { IconUpload } from '@components/icons/upload';
import { Stack } from '@components/stack';
import { VisuallyHidden } from '@components/visually-hidden';
import { theme } from '@styles/theme';
import { getBase64 } from '@utils/browser';

const VALID_PICTURE_TYPES = ['image/jpeg', 'image/png'];
export const PROFILE_IMAGE_SIZES = ['small', 'large'] as const;
export type ProfileImageSize = (typeof PROFILE_IMAGE_SIZES)[number];

export type AddProfileImageProps = {
  name?: string;
  initalPicture?: string;
  picture?: File | null;
  setPicture: (picture: File | null) => void;
  clearPicture?: boolean;
  setClearPicture: (clearPicture: boolean) => void;
  setIsPictureInvalid?: (isInvalid: boolean) => void;
  size?: ProfileImageSize;
};

export const AddProfileImage = ({
  name,
  initalPicture,
  picture,
  setPicture,
  clearPicture,
  setClearPicture,
  setIsPictureInvalid,
  size = 'small',
}: AddProfileImageProps) => {
  const [pictureBase64, setPictureBase64] = useState('');

  async function changePicture(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target?.files?.[0];
    if (file) {
      let newPicture = null;

      try {
        newPicture = await getBase64(file);
      } catch (e) {}
      setPictureBase64(newPicture as string);
      setPicture(file);
      setClearPicture(false);
      setIsPictureInvalid?.(VALID_PICTURE_TYPES.includes(file.type) ? false : true);
    }
  }

  const removePicture = () => {
    setPictureBase64('');
    setClearPicture(true);
    setIsPictureInvalid?.(false);
    setPicture(null);
  };

  return (
    <ProfilePicContainer size={size}>
      <ProfilePicItem isOver={true}>
        <EditCircle
          hasImage={Boolean(picture) || (Boolean(initalPicture) && !clearPicture)}
          changePicture={changePicture}
          removePicture={removePicture}
          size={size}
        />
      </ProfilePicItem>
      <ProfilePicItem>
        {!name && !picture ? (
          <EmptyProfilePic size={size} />
        ) : (
          <Avatar
            size={size === 'large' ? 'xxxl' : 'xxl'}
            name={name ?? ''}
            src={pictureBase64 || (!clearPicture ? initalPicture : '')}
          />
        )}
      </ProfilePicItem>
    </ProfilePicContainer>
  );
};

const EmptyProfilePic = ({ size }: { size: ProfileImageSize }) => {
  return (
    <BackgroundCircle background={true} size={size}>
      <IconImage color="uiTrans50" size={32} />
    </BackgroundCircle>
  );
};

type EditCircleProps = {
  hasImage: boolean;
  changePicture: (event: ChangeEvent<HTMLInputElement>) => void;
  removePicture: () => void;
  size: ProfileImageSize;
};
const EditCircle = ({ hasImage, changePicture, removePicture, size }: EditCircleProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const clickUpload = () => {
    fileInputRef?.current?.click();
  };

  const handleRemove = () => {
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
    removePicture();
  };

  return (
    <BackgroundCircle id="background-circle" size={size}>
      {hasImage ? (
        <Stack space={2} orientation="horizontal">
          <IconEdit color="white100" onClick={clickUpload} style={{ cursor: 'pointer' }} />

          <IconRemove color="white100" onClick={handleRemove} style={{ cursor: 'pointer' }} />
        </Stack>
      ) : (
        <IconUpload color="white100" onClick={clickUpload} style={{ cursor: 'pointer' }} />
      )}
      <VisuallyHidden>
        <input
          ref={fileInputRef}
          name="file"
          type="file"
          accept="image/*"
          onChange={changePicture}
        />
      </VisuallyHidden>
    </BackgroundCircle>
  );
};

const BackgroundCircle = styled.div<{ size: ProfileImageSize; background?: boolean }>((p) => ({
  height: p.size === 'large' ? '8rem' : '4rem',
  width: p.size === 'large' ? '8rem' : '4rem',
  backgroundColor: p.background ? theme.color.uiTrans5 : theme.color.purpleTrans80,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'opacity 150ms ease-out',
}));

const ProfilePicContainer = styled.div<{ size: ProfileImageSize }>((p) => ({
  width: p.size === 'large' ? '8rem' : '4rem',
  height: p.size === 'large' ? '8rem' : '4rem',
  position: 'relative',
  '&:not(:hover) #background-circle': {
    opacity: 0,
  },
  '&:hover #background-circle': {
    opacity: 1,
  },
}));

const ProfilePicItem = styled.div<{ isOver?: boolean }>((p) => ({
  position: 'absolute',
  zIndex: p.isOver ? 2 : 1,
}));
